/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Box, Stack, Text, useColorModeValue } from '@chakra-ui/react';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { InlineNumberInput, InlineSlider, StackedStringInput } from './controls';
import { Components, PanelItem, Size, useSubscription } from '@rta/rta-blueprint-library';
import { useEffect, useState, useCallback, type JSX } from 'react';

export type AppliancePropertiesPanelProps = {
	id: string | null;
};

export const CutoutPropertiesPanel = ({ id }: AppliancePropertiesPanelProps): JSX.Element => {
	const [controller] = useDesign<BpController>();

	const [label, setLabel] = useState<string>('');
	const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
	const [itemSize, setItemSize] = useState<Size | null>(null);

	// Subscribe to item_sku, item_size, and item_cutout updates
	const [event] = useSubscription(controller?.kitchen ?? null, true, [
		['item_sku', 'updated'],
		['item_size', 'updated'],
		['item_cutout', 'updated'],
		['component_size', 'updated'],
		['component_cutout', 'updated']
	]);
	const readonly = controller?.readonly ?? true;

	useEffect(() => {
		if (panelItem && event) {
			if (event.noun === 'item_sku') setLabel(panelItem.item.sku);
			if (event.noun === 'item_size' || event.noun === 'component_size') {
				setItemSize({ ...panelItem.item.size });
			}
			if (event.noun === 'item_cutout' || event.noun === 'component_cutout') {
				setItemSize({ ...panelItem.item.size });
			}
		}
	}, [event, panelItem]);

	useEffect(() => {
		if (controller) {
			const refMap = controller.kitchen?.getReferenceMap(id ?? '');
			setPanelItem(refMap.panelItem!);
			setLabel(refMap.panelItem!.item.sku);
			setItemSize({ ...refMap.panelItem!.item.size });
		}
	}, [controller, id]);

	const handleDiameterChange = useCallback((diameter: number) => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { width: diameter, height: diameter, depth: diameter };
		panelItem.item.cutout = { width: diameter, height: diameter, depth: diameter };
		setItemSize({ width: diameter, height: diameter, depth: diameter });
	}, [panelItem]);

	const handleWidthChange = useCallback((width: number) => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { width };
		panelItem.item.cutout = { width };
		setItemSize(prev => prev ? { ...prev, width } : null);
	}, [panelItem]);

	const handleHeightChange = useCallback((height: number) => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { height };
		panelItem.item.cutout = { height };
		setItemSize(prev => prev ? { ...prev, height } : null);
	}, [panelItem]);

	const handleDepthChange = useCallback((depth: number) => {
		if (!panelItem) return;
		if (!panelItem.item.override) {
			panelItem.item.override = true;
		}
		panelItem.item.size = { depth };
		panelItem.item.cutout = { depth };
		setItemSize(prev => prev ? { ...prev, depth } : null);
	}, [panelItem]);

	const labelColor = useColorModeValue('gray.600', 'gray.300');

	if (!controller || panelItem === null || itemSize === null) return <></>;

	const isCountertopCutout = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;

	return <ContextPanelContent title={lang.contextPanels.title.cutoutProperties}>
		<Stack pt="10px" spacing="0" width="100%">
			<StackedStringInput
				label="Label"
				placeholder="Label or SKU..."
				enabled={!readonly}
				value={panelItem.item.component?.customLabel ?? ''}
				onChange={(e) => {
					if (panelItem.item.component) panelItem.item.component.customLabel = e.target.value;
				}}
			/>

			<Text pt="5px" pl="5px" color={labelColor} fontSize="medium"
				fontWeight="medium"
			>Dimensional Details</Text>
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				{/* Special handling for circular cutouts (hole category) - show single "Diameter" field */}
				{panelItem.item.category?.key === 'hole' ? (
					<InlineNumberInput
						label="Diameter" min={0.5}
						step={0.125}
						value={itemSize.width}
						enabled={!readonly}
						onChange={(valueString) => {
							handleDiameterChange(Number(valueString));
						}}
					/>
				) : (
					<>
						<InlineNumberInput
							label="Width" min={0.5}
							value={itemSize.width}
							enabled={!readonly}
							onChange={(valueString) => {
								handleWidthChange(Number(valueString));
							}}
						/>
						<InlineNumberInput
							label={isCountertopCutout ? 'Depth' : 'Height'} min={0.5}
							value={isCountertopCutout ? itemSize.depth : itemSize.height}
							enabled={!readonly}
							onChange={(valueString) => {
								if (isCountertopCutout) handleDepthChange(Number(valueString));
								else handleHeightChange(Number(valueString));
							}}
						/>
					</>
				)}
			</Box>
			<>
				<Text pl="5px" color={labelColor} fontSize="medium"
					fontWeight="medium"
				>Dimension Positioning</Text>
				<Box marginBottom={3} padding={2} textAlign="left"
					borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
				>
					<Stack spacing="3" width="100%" height="65%">
						<InlineSlider
							category="dimension" label="H. Line"
							min={0} max={1} step={0.01}
							value={ panelItem.dimensionOffsets.hLine }
							enabled={!readonly}
							onChange={(e) => {
								panelItem.dimensionOffsets = { hLine: e };
							}}
						/>
						<InlineSlider
							category="dimension" label="H. Label"
							min={0} max={1} step={0.01}
							value={ panelItem.dimensionOffsets.hLabel }
							enabled={!readonly}
							onChange={(e) => {
								panelItem.dimensionOffsets = { hLabel: e };
							}}
						/>
						<InlineSlider
							category="dimension" label="V. Line"
							min={0} max={1} step={0.01}
							value={ panelItem.dimensionOffsets.vLine }
							enabled={!readonly}
							onChange={(e) => {
								panelItem.dimensionOffsets = { vLine: e };
							}}
						/>
						<InlineSlider
							category="dimension" label="V. Label"
							min={0} max={1} step={0.01}
							value={ panelItem.dimensionOffsets.vLabel }
							enabled={!readonly}
							onChange={(e) => {
								panelItem.dimensionOffsets = { vLabel: e };
							}}
						/>
					</Stack>
				</Box>
			</>
		</Stack>
	</ContextPanelContent>;
};
